import { useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { useState } from 'react'
import { employeesQueryOptions, shortName } from '@/entities/employee'
import { excursionsQueryOptions } from '@/entities/excursion'
import { useSession } from '@/entities/session'
import {
  compareByTime,
  scheduleQueryOptions,
  type Task,
  useUpdateTaskMutation,
} from '@/entities/task'
import { TaskDialog, type TaskDialogState } from '@/features/task-editor'
import { addDays, cn, fmtInterval, startOfDay } from '@/shared/lib'
import { Button } from '@/shared/ui/button'
import { Checkbox } from '@/shared/ui/checkbox'
import { EmptyState } from '@/shared/ui/empty-state'
import { PageBody } from '@/shared/ui/page-body'
import { PageHeader } from '@/shared/ui/page-header'
import { currentMonthPeriod, PeriodPicker, type PeriodValue } from '@/shared/ui/period-picker'
import { Segmented } from '@/shared/ui/segmented'
import { Spinner } from '@/shared/ui/spinner'

type Scope = 'team' | 'me'

/**
 * Дела своим разделом, а не панелью в расписании: на телефоне список отжимал
 * сетку, а сетка и есть ответ на «кто когда занят». Закрытое время сюда не
 * попадает вовсе — оно живёт только в расписании.
 */
export const Route = createFileRoute('/_app/tasks')({
  component: TasksPage,
})

function TasksPage() {
  const { session } = useSession()
  const [period, setPeriod] = useState<PeriodValue>(() => currentMonthPeriod())
  const [scope, setScope] = useState<Scope>('team')
  const [dialog, setDialog] = useState<TaskDialogState | null>(null)

  const teamWide = session?.isAdmin === true
  const mine = (!teamWide || scope === 'me') && session ? [session.employeeId] : null

  const from = startOfDay(new Date(`${period.from}T00:00:00`))
  const to = addDays(startOfDay(new Date(`${period.to}T00:00:00`)), 1)

  const employeesQuery = useQuery(employeesQueryOptions())
  const scheduleQuery = useQuery(scheduleQueryOptions({ from, to, includeUntimed: true }))
  const excursionsQuery = useQuery(excursionsQueryOptions({ from, to, withBookings: false }))
  const updateTask = useUpdateTaskMutation()

  const employees = employeesQuery.data ?? []
  const all = [...(scheduleQuery.data?.items ?? []), ...(scheduleQuery.data?.untimed ?? [])]
  const tasks = (
    mine ? all.filter((t) => t.assigneeIds.some((id) => mine.includes(id))) : all
  ).sort(compareByTime)

  // Дела без времени — отдельной группой: это не черновики, а задачи, осознанно
  // не попавшие в сетку, и в общем списке по дате им места нет.
  const timed = tasks.filter((task) => task.slot !== null)
  const untimed = tasks.filter((task) => task.slot === null)

  const openTask = (task: Task) => {
    setDialog({
      task,
      slot: task.slot,
      assigneeIds: task.assigneeIds,
      excursionId: task.excursionId,
    })
  }

  const assigneeNames = (task: Task) =>
    task.assigneeIds
      .map((id) => {
        const employee = employees.find((e) => e.id === id)
        return employee ? shortName(employee) : '—'
      })
      .join(', ') || 'ничьё'

  return (
    <PageBody>
      <PageHeader
        title="Дела"
        actions={
          <Button
            size="sm"
            onClick={() =>
              setDialog({ task: null, slot: null, assigneeIds: mine ?? [], excursionId: null })
            }
          >
            Новое дело
          </Button>
        }
      />

      <div className="mb-5 flex flex-wrap items-center gap-3">
        <PeriodPicker value={period} onChange={setPeriod} />
        {teamWide && (
          <Segmented
            ariaLabel="Чьи дела"
            value={scope}
            onChange={setScope}
            options={[
              { value: 'team', label: 'вся команда' },
              { value: 'me', label: 'только мои' },
            ]}
          />
        )}
      </div>

      {scheduleQuery.isLoading ? (
        <Spinner />
      ) : tasks.length === 0 ? (
        <EmptyState
          title="За период дел нет"
          description="Заведите дело — со временем оно встанет в расписание, без времени останется здесь."
        />
      ) : (
        <div className="flex flex-col gap-6">
          {timed.length > 0 && (
            <TaskGroup
              title="По времени"
              tasks={timed}
              names={assigneeNames}
              onOpen={openTask}
              onToggle={(task) => updateTask.mutate({ id: task.id, patch: { done: !task.done } })}
            />
          )}
          {untimed.length > 0 && (
            <TaskGroup
              title="Без времени"
              tasks={untimed}
              names={assigneeNames}
              onOpen={openTask}
              onToggle={(task) => updateTask.mutate({ id: task.id, patch: { done: !task.done } })}
            />
          )}
        </div>
      )}

      <TaskDialog
        state={dialog}
        employees={employees}
        excursions={excursionsQuery.data ?? []}
        day={from}
        onClose={() => setDialog(null)}
      />
    </PageBody>
  )
}

interface TaskGroupProps {
  title: string
  tasks: Task[]
  names: (task: Task) => string
  onOpen: (task: Task) => void
  onToggle: (task: Task) => void
}

function TaskGroup({ title, tasks, names, onOpen, onToggle }: TaskGroupProps) {
  return (
    <section aria-label={title} className="flex flex-col gap-2">
      <h2 className="label">
        {title}
        <span className="ml-1.5 text-text-tertiary">{tasks.length}</span>
      </h2>
      <ul className="flex flex-col divide-y divide-border-subtle">
        {tasks.map((task) => (
          <li
            key={task.id}
            className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-control px-2 py-3 transition-colors hover:bg-bg-elevated"
          >
            <Checkbox
              checked={task.done}
              onChange={() => onToggle(task)}
              aria-label={task.done ? `Вернуть в работу: ${task.title}` : `Отметить: ${task.title}`}
            />
            {/* На узком экране строка распадается на карточку: название сверху
                целиком, время и исполнители под ним. */}
            <button
              type="button"
              onClick={() => onOpen(task)}
              className="order-1 min-w-0 flex-1 basis-40 text-left"
            >
              <span
                className={cn(
                  'block truncate text-sm',
                  task.done ? 'text-text-tertiary line-through' : 'text-text-primary',
                )}
              >
                {task.title}
              </span>
              {task.note && (
                <span className="block truncate text-xs text-text-tertiary">{task.note}</span>
              )}
            </button>
            <span className="mono-num order-3 shrink-0 text-xs whitespace-nowrap text-text-tertiary sm:order-2 sm:w-56">
              {task.slot ? fmtInterval(task.slot) : ''}
            </span>
            <span className="order-4 max-w-40 shrink-0 truncate text-xs text-text-tertiary sm:order-3">
              {names(task)}
            </span>
          </li>
        ))}
      </ul>
    </section>
  )
}
